import React, { useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import max from 'lodash/max'
import sum from 'lodash/sum'
import toNumber from 'lodash/toNumber'
import truncate from 'lodash/truncate'
import { Stack } from 'react-bootstrap'
import { useUpdateEffect } from 'react-use'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { RefCallback } from 'react'
import type { SelectOption } from '@ztechapps/webui.input'
import type { PivotCellTextRenderer } from '../constants/types'
import type { PivotExpandState, PivotResult } from '../pivot/types'
import { PivotChartTypeEnum } from '@ztechapps/common.contract'
import { getWindow, hasWindow } from '@ztechapps/common.utils'
import { Select } from '@ztechapps/webui.input'
import { EmptyContent, ShowIf } from '@ztechapps/webui.util'
import {
  PIVOT_CHART_CATEGORY_MIN_WIDTH,
  PIVOT_CHART_CONTROLS_HEIGHT,
  PIVOT_CHART_SERIES_COUNT,
  PIVOT_CHART_TYPE_LABELS,
} from '../pivot/constants'
import { formatPivotValue } from '../pivot/formatPivotValue'
import { toPivotMeasureLabel } from '../pivot/toPivotMeasureLabel'
import { usePivotViewerContext } from '../PivotViewer/Context'
import { buildPivotChartData } from './buildPivotChartData'

/** Props for the pivot chart body. */
export type PivotChartProps = {
  result: PivotResult
  expandState: PivotExpandState
  chartType: PivotChartTypeEnum
  maxHeight: string
  renderCellText?: PivotCellTextRenderer
  /**
   * Receives the full-width chart element an export captures — the scroll container around it clips — and null
   * when that element unmounts, which is every time an empty state shows.
   */
  chartRef: RefCallback<HTMLDivElement>
}

// above this many categories the X labels tilt so they stay readable
const ANGLED_LABEL_THRESHOLD = 6
// recharts draws an axis line and its tick marks '#666' unless told otherwise
const AXIS_PROPS = { tick: { fill: 'var(--bs-body-color)' }, stroke: 'var(--bs-border-color)' }
// recharts defaults an item's text to its series colour (a fill token) and the hover band or line to '#ccc'
const TOOLTIP_PROPS = {
  contentStyle: {
    backgroundColor: 'var(--bs-body-bg)',
    borderColor: 'var(--bs-border-color)',
    color: 'var(--bs-body-color)',
  },
  itemStyle: { color: 'var(--bs-body-color)' },
  cursor: { fill: 'var(--bs-secondary-bg)', stroke: 'var(--bs-border-color)' },
}
// the Pie slice separator and the Line dot, which recharts defaults to '#fff'
const SURFACE_COLOR = 'var(--bs-body-bg)'
// recharts 2 gives a Y axis a fixed box (60px by default) and clips a tick label that overruns it, so the box is
// sized from the longest label the axis can show
const Y_AXIS_MIN_WIDTH = 60
// px per label character at the 0.8rem tick font, rounded up
const AXIS_CHAR_WIDTH = 8
// the tick mark and its margin, plus a pad
const Y_AXIS_TICK_ALLOWANCE = 12
// extra characters for a sign, parentheses or a tick the library rounds up a digit
const Y_AXIS_LABEL_SLACK = 1
// an angled X label hangs down-left of its tick at 45°, so the axis box is the longest label's length times sin 45°
// plus the tick mark, its margin and one line of text
const X_AXIS_TICK_ALLOWANCE = 24
// an angled X label is cut to this many characters, the tooltip keeps the whole label, so a long label can neither
// clip at the box nor squeeze the plot away
const X_AXIS_MAX_LABEL_LENGTH = 32
// recharts' default chart margin, which a margin prop replaces whole rather than merges
const CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: 5 }

// a legend with many series (or pie slices) would otherwise grow until it squeezes the plot away, so it is capped at
// roughly four rows and scrolls
const LEGEND_WRAPPER_STYLE = { maxHeight: 80, overflowY: 'auto' as const }

// recharts paints a legend label in its series colour, which only clears the 3:1 non-text floor — the label takes the
// body colour and the swatch keeps the series colour
const renderLegendText = (value: string) => <span className="text-body">{value}</span>

// Literal colour strings, read once per render, so a PNG/PDF export never depends on var() resolving.
const readSeriesPalette = (): string[] => {
  if (!hasWindow()) return []
  const win = getWindow()
  const styles = win.getComputedStyle(win.document.documentElement)
  return Array.from({ length: PIVOT_CHART_SERIES_COUNT }, (_, index) =>
    styles.getPropertyValue(`--chart-series-${index + 1}`).trim()
  )
}

/** Column, stacked column, pie or column-line view of the pivot rows at their current depth. */
export const PivotChart = ({
  result,
  expandState,
  chartType,
  maxHeight,
  renderCellText,
  chartRef,
}: PivotChartProps) => {
  const { container } = usePivotViewerContext()
  const [pickedMeasureIndex, setPickedMeasureIndex] = useState(0)
  // a pick belongs to the values it was made over
  useUpdateEffect(() => setPickedMeasureIndex(0), [result.measures.length])
  // the reset lands after the render that first sees the new count, so that render clamps a stale pick
  const measureIndex = Math.min(pickedMeasureIndex, Math.max(result.measures.length - 1, 0))

  const { series, rows, pieSlices, droppedCount } = useMemo(
    () => buildPivotChartData({ result, expandState, chartType, measureIndex, renderCellText }),
    [result, expandState, chartType, measureIndex, renderCellText]
  )

  if (isEmpty(result.measures)) return <EmptyContent message="Add fields to Values to chart" />
  if (isEmpty(rows)) return <EmptyContent message="No rows to chart" />

  const isPie = chartType === PivotChartTypeEnum.Pie
  const isColumnLine = chartType === PivotChartTypeEnum.ColumnLine
  const isStacked = chartType === PivotChartTypeEnum.StackedColumn
  const palette = readSeriesPalette()
  const colorAt = (index: number) => palette[index % PIVOT_CHART_SERIES_COUNT]

  const measureOptions: SelectOption[] = result.measures.map((measure, index) => ({
    value: `${index}`,
    label: toPivotMeasureLabel(measure),
  }))
  const formatSeries = (value: unknown, dataKey: unknown): string => {
    const seriesMeasure = series.find((entry) => entry.key === dataKey)?.measureIndex ?? measureIndex
    return formatPivotValue(toNumber(value), result.measureFormats[seriesMeasure])
  }
  // the widest tick is the largest plotted magnitude, which for a stack is the row's total
  const toYAxisWidth = (keys: string[]): number => {
    const magnitudes = rows.map((row) => {
      const values = keys.map((key) => Math.abs(toNumber(row[key])))
      return isStacked ? sum(values) : (max(values) ?? 0)
    })
    const labelLength = formatSeries(max(magnitudes) ?? 0, keys[0]).length
    return Math.max(Y_AXIS_MIN_WIDTH, (labelLength + Y_AXIS_LABEL_SLACK) * AXIS_CHAR_WIDTH + Y_AXIS_TICK_ALLOWANCE)
  }

  const chartName = PIVOT_CHART_TYPE_LABELS[chartType]
  const summary = isColumnLine
    ? `${chartName} chart of ${series[0].label} and ${series[1].label}, ${rows.length} categories`
    : `${chartName} chart of ${toPivotMeasureLabel(result.measures[measureIndex])}, ${rows.length} categories`
  const isAngled = !isPie && rows.length > ANGLED_LABEL_THRESHOLD
  const toXAxisHeight = (): number => {
    const labelLength = Math.min(max(rows.map((row) => String(row.category).length)) ?? 0, X_AXIS_MAX_LABEL_LENGTH)
    return Math.ceil(labelLength * AXIS_CHAR_WIDTH * Math.SQRT1_2) + X_AXIS_TICK_ALLOWANCE
  }
  // the first angled label hangs left of its tick by its length times sin 45°; the Y axis and half a category sit
  // left of that tick already (a category is never narrower than its minimum), the margin covers the rest
  const toChartMargin = (yAxisWidth: number) => {
    if (!isAngled) return CHART_MARGIN
    const firstLabelLength = Math.min(String(rows[0].category).length, X_AXIS_MAX_LABEL_LENGTH)
    const overhang = Math.ceil(firstLabelLength * AXIS_CHAR_WIDTH * Math.SQRT1_2) - PIVOT_CHART_CATEGORY_MIN_WIDTH / 2
    return { ...CHART_MARGIN, left: Math.max(CHART_MARGIN.left, overhang - yAxisWidth) }
  }
  const legendAlign = isAngled ? 'left' : 'center'
  const xAxisProps = {
    dataKey: 'category',
    interval: 0,
    ...AXIS_PROPS,
    ...(isAngled
      ? {
          angle: -45,
          textAnchor: 'end' as const,
          height: toXAxisHeight(),
          tickFormatter: (value: string) => truncate(value, { length: X_AXIS_MAX_LABEL_LENGTH }),
        }
      : {}),
  }
  const minWidth = isPie ? undefined : rows.length * PIVOT_CHART_CATEGORY_MIN_WIDTH

  return (
    <Stack gap={2}>
      <ShowIf condition={!isColumnLine}>
        <div style={{ maxWidth: 360 }}>
          <Select
            aria-label="Chart value"
            isClearable={false}
            options={measureOptions}
            value={measureOptions[measureIndex]}
            onChange={(option: SelectOption) => setPickedMeasureIndex(toNumber(option.value))}
            menuPortalTarget={container}
          />
        </div>
      </ShowIf>
      <ShowIf condition={isPie && droppedCount > 0}>
        <small className="text-body-secondary">{droppedCount} values ≤0 not shown</small>
      </ShowIf>
      {isPie && isEmpty(pieSlices) ? (
        <EmptyContent message="No positive values to chart" />
      ) : (
        <div className="overflow-x-auto" style={{ height: `calc(${maxHeight} - ${PIVOT_CHART_CONTROLS_HEIGHT})` }}>
          <div ref={chartRef} role="img" aria-label={summary} className="h-100" style={{ minWidth }}>
            <ResponsiveContainer width="100%" height="100%">
              {isPie ? (
                <PieChart accessibilityLayer>
                  <Pie data={pieSlices} dataKey="value" nameKey="name" stroke={SURFACE_COLOR} isAnimationActive={false}>
                    {pieSlices.map((slice, index) => (
                      <Cell key={slice.name} fill={colorAt(index)} />
                    ))}
                  </Pie>
                  <Tooltip {...TOOLTIP_PROPS} formatter={(value, name) => [formatSeries(value, undefined), name]} />
                  <Legend formatter={renderLegendText} wrapperStyle={LEGEND_WRAPPER_STYLE} />
                </PieChart>
              ) : isColumnLine ? (
                <ComposedChart accessibilityLayer data={rows} margin={toChartMargin(toYAxisWidth([series[0].key]))}>
                  <CartesianGrid vertical={false} stroke="var(--bs-border-color)" />
                  <XAxis {...xAxisProps} />
                  <YAxis
                    yAxisId="left"
                    width={toYAxisWidth([series[0].key])}
                    {...AXIS_PROPS}
                    tickFormatter={(value) => formatSeries(value, series[0].key)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={toYAxisWidth([series[1].key])}
                    {...AXIS_PROPS}
                    tickFormatter={(value) => formatSeries(value, series[1].key)}
                  />
                  <Tooltip
                    {...TOOLTIP_PROPS}
                    formatter={(value, name, item) => [formatSeries(value, item.dataKey), name]}
                  />
                  <Legend align={legendAlign} formatter={renderLegendText} />
                  <Bar
                    yAxisId="left"
                    dataKey={series[0].key}
                    name={series[0].label}
                    fill={colorAt(0)}
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="right"
                    dataKey={series[1].key}
                    name={series[1].label}
                    stroke={colorAt(1)}
                    strokeWidth={2}
                    dot={{ fill: SURFACE_COLOR }}
                    activeDot={{ stroke: SURFACE_COLOR }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              ) : (
                <BarChart
                  accessibilityLayer
                  data={rows}
                  margin={toChartMargin(toYAxisWidth(series.map((entry) => entry.key)))}
                >
                  <CartesianGrid vertical={false} stroke="var(--bs-border-color)" />
                  <XAxis {...xAxisProps} />
                  <YAxis
                    width={toYAxisWidth(series.map((entry) => entry.key))}
                    {...AXIS_PROPS}
                    tickFormatter={(value) => formatSeries(value, undefined)}
                  />
                  <Tooltip
                    {...TOOLTIP_PROPS}
                    formatter={(value, name, item) => [formatSeries(value, item.dataKey), name]}
                  />
                  <Legend align={legendAlign} formatter={renderLegendText} wrapperStyle={LEGEND_WRAPPER_STYLE} />
                  {series.map((entry, index) => (
                    <Bar
                      key={entry.key}
                      dataKey={entry.key}
                      name={entry.label}
                      fill={colorAt(index)}
                      stackId={isStacked ? 'total' : undefined}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Stack>
  )
}
